import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CreditCard, Smartphone, Building2, Copy, Check, QrCode } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const PaymentSection = ({ t }) => {
  const [copiedField, setCopiedField] = useState(null);
  const [showQR, setShowQR] = useState(false);

  const bankDetails = {
    bankName: 'Punjab National Bank',
    branch: 'Civil Lines, Aligarh (UP) - 202001',
    ifsc: 'PUNB0051100',
    accountNo: '0511000100354468',
    accountHolder: 'IMRAN',
    accountType: 'Savings Account'
  };

  const upiId = '8307534087@ptsbi';
  const qrCodeUrl = 'https://customer-assets.emergentagent.com/job_legal-portfolio-12/artifacts/scfnn1gy_IMG-20260205-WA0002.jpg';

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast({
      title: t?.copied || "Copied!",
      description: `${field} copied to clipboard`,
    });
    setTimeout(() => setCopiedField(null), 2000);
  };

  const isHindi = t?.nav_home === 'मुख्य पृष्ठ';

  return (
    <section id="payment" className="py-10 bg-gradient-to-br from-emerald-50 to-teal-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center space-x-2 bg-emerald-600 text-white px-4 py-1.5 rounded-full mb-3 shadow-lg text-sm">
              <CreditCard className="h-4 w-4" />
              <span className="font-semibold">
                {isHindi ? 'ऑनलाइन भुगतान स्वीकृत' : 'Online Payment Accepted'}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
              {isHindi ? 'भुगतान विकल्प' : 'Payment Options'}
            </h2>
            <p className="text-gray-600 text-sm max-w-xl mx-auto">
              {isHindi 
                ? 'प्रारंभिक परामर्श मुफ्त है। आगे की सेवाओं के लिए नीचे से भुगतान करें।'
                : 'Initial consultation is FREE. For further services, pay using options below.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {/* UPI Payment */}
            <Card className="overflow-hidden border border-emerald-200 hover:border-emerald-400 transition-colors">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-3">
                <div className="flex items-center space-x-2 text-white">
                  <Smartphone className="h-5 w-5" />
                  <h3 className="text-base font-bold">
                    {isHindi ? 'UPI से भुगतान' : 'Pay via UPI'}
                  </h3>
                </div>
              </div>
              <CardContent className="p-4">
                {/* UPI ID */}
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">{isHindi ? 'UPI आईडी' : 'UPI ID'}</p>
                  <div className="flex items-center justify-between bg-gray-100 p-2 rounded-lg">
                    <span className="font-mono font-semibold text-gray-800 text-sm">{upiId}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => copyToClipboard(upiId, 'UPI ID')}
                      className="hover:bg-emerald-100 h-7 w-7 p-0"
                    >
                      {copiedField === 'UPI ID' ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                </div>

                {/* QR Code Toggle */}
                <Button 
                  onClick={() => setShowQR(!showQR)} 
                  variant="outline" 
                  size="sm"
                  className="w-full mb-3 border-emerald-300 hover:bg-emerald-50 text-xs"
                >
                  <QrCode className="h-3 w-3 mr-1" />
                  {showQR 
                    ? (isHindi ? 'QR छुपाएं' : 'Hide QR') 
                    : (isHindi ? 'QR दिखाएं' : 'Show QR')}
                </Button>

                {/* QR Code Image */}
                {showQR && (
                  <div className="bg-white p-3 rounded-lg border border-dashed border-emerald-300 text-center">
                    <img 
                      src={qrCodeUrl} 
                      alt="UPI QR Code" 
                      className="mx-auto max-w-[140px] rounded-lg shadow-sm"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {isHindi ? 'UPI ऐप से स्कैन करें' : 'Scan with UPI app'}
                    </p>
                  </div>
                )}

                <div className="flex flex-wrap gap-1.5 justify-center mt-3">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Paytm</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">PhonePe</span>
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">GPay</span>
                </div>
              </CardContent>
            </Card>

            {/* Bank Transfer */}
            <Card className="overflow-hidden border border-blue-200 hover:border-blue-400 transition-colors">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3">
                <div className="flex items-center space-x-2 text-white">
                  <Building2 className="h-5 w-5" />
                  <h3 className="text-base font-bold">
                    {isHindi ? 'बैंक ट्रांसफर' : 'Bank Transfer'}
                  </h3>
                </div>
              </div>
              <CardContent className="p-4 space-y-2">
                {/* Bank Name */}
                <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                  <span className="text-xs text-gray-500">{isHindi ? 'बैंक' : 'Bank'}</span>
                  <span className="font-semibold text-gray-800 text-xs">{bankDetails.bankName}</span>
                </div>

                {/* Account Holder */}
                <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                  <span className="text-xs text-gray-500">{isHindi ? 'खाताधारक' : 'Holder'}</span>
                  <span className="font-semibold text-gray-800 text-sm">{bankDetails.accountHolder}</span>
                </div>

                {/* Account Number */}
                <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                  <span className="text-xs text-gray-500">{isHindi ? 'खाता नं.' : 'A/C No.'}</span>
                  <div className="flex items-center space-x-1">
                    <span className="font-mono font-semibold text-gray-800 text-xs">{bankDetails.accountNo}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => copyToClipboard(bankDetails.accountNo, 'Account No')}
                      className="hover:bg-blue-100 h-6 w-6 p-0"
                    >
                      {copiedField === 'Account No' ? <Check className="h-3 w-3 text-blue-600" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                </div>

                {/* IFSC Code */}
                <div className="flex justify-between items-center py-1.5 border-b border-gray-100">
                  <span className="text-xs text-gray-500">IFSC</span>
                  <div className="flex items-center space-x-1">
                    <span className="font-mono font-semibold text-gray-800 text-xs">{bankDetails.ifsc}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => copyToClipboard(bankDetails.ifsc, 'IFSC')}
                      className="hover:bg-blue-100 h-6 w-6 p-0"
                    >
                      {copiedField === 'IFSC' ? <Check className="h-3 w-3 text-blue-600" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                </div>

                {/* Branch */}
                <div className="flex justify-between items-center py-1.5">
                  <span className="text-xs text-gray-500">{isHindi ? 'शाखा' : 'Branch'}</span>
                  <span className="font-semibold text-gray-800 text-xs text-right">Civil Lines, Aligarh</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Note */}
          <div className="mt-5 text-center">
            <p className="text-xs text-gray-500 bg-amber-50 inline-block px-4 py-2 rounded-full border border-amber-200">
              💡 {isHindi 
                ? 'भुगतान के बाद रसीद WhatsApp पर भेजें'
                : 'After payment, send receipt on WhatsApp'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PaymentSection;
